/**
 * GeneralLedgerContext — canonical book of record (Phase 1).
 *
 * Source of truth for:
 *   • Chart of Accounts
 *   • Journal Entries (append-only; void by reversing entry, never edit posted)
 *
 * Posting invariants enforced at write time:
 *   • lines.length >= 2
 *   • sum(debits) === sum(credits)  (to the cent)
 *   • every line.accountId exists and account.active
 *   • subledger-tagged accounts require their dimension on the line
 *
 * Selectors compute Trial Balance + per-account ledger detail on demand.
 */
import {
  createContext, useCallback, useContext, useMemo, useRef, useState,
} from "react";
import type {
  AccountBalance, GLAccount, JournalEntry, JournalLine, JournalSource,
} from "./glTypes";
import { SEED_CHART_OF_ACCOUNTS } from "./chartOfAccounts";

const CENT = (n: number) => Math.round(n * 100);
const ymKey = (iso: string) => iso.slice(0, 7); // "2026-06"
/** Last day of a yyyy-mm period as yyyy-mm-dd, UTC-safe (no DST drift). */
const lastDayOfPeriod = (period: string): string => {
  const [y, m] = period.split("-").map(Number);
  // Day 0 of next month = last day of current month, computed in UTC.
  const d = new Date(Date.UTC(y, m, 0));
  return d.toISOString().slice(0, 10);
};
/** yyyy-mm of the month after `period`. */
const nextPeriod = (period: string): string => {
  const [y, m] = period.split("-").map(Number);
  const d = new Date(Date.UTC(y, m, 1)); // m is 1-indexed → Date.UTC(y, m, 1) = first of next month
  return d.toISOString().slice(0, 7);
};
/** First day of a yyyy-mm period as yyyy-mm-dd. */
const firstDayOfPeriod = (period: string): string => `${period}-01`;

interface NewLineInput {
  accountId: string;
  amount: number;
  side: "debit" | "credit";
  memo?: string;
  customerId?: string;
  vendorId?: string;
  jobId?: string;
  tradeType?: string;
  wipBucket?: "material" | "labor";
}

interface PostJournalInput {
  postingDate: string;       // ISO yyyy-mm-dd
  description: string;
  source?: JournalSource;
  sourceRef?: { kind: string; id: string };
  createdBy?: string;
  lines: NewLineInput[];
  /** ISO-4217. Defaults to "USD". Stamped for Supabase forward-compat. */
  currency?: string;
  /** Optional dedupe key. If omitted, auto-derived from source+sourceRef.
   *  Re-posting with the same key is a no-op (returns the existing entry). */
  idempotencyKey?: string;
}

/** Default currency for all postings until multi-currency lands. */
const DEFAULT_CURRENCY = "USD";

/** Account numbers that are roll-up parents only — direct posting is blocked. */
const ROLLUP_PARENT_ACCOUNTS = new Set(["4000", "5000", "5500"]);

/** Build an idempotency key from explicit input or derive from source+ref. */
function deriveIdempotencyKey(input: PostJournalInput): string | undefined {
  if (input.idempotencyKey) return input.idempotencyKey;
  if (input.source && input.sourceRef) {
    return `${input.source}:${input.sourceRef.kind}:${input.sourceRef.id}`;
  }
  return undefined;
}

export interface PeriodLock {
  period: string;            // "2026-06"
  status: "open" | "closed";
  closedAt?: string;
  closedBy?: string;
  /** JE id of the retained-earnings roll posted on close (reversed if reopened). */
  rollEntryId?: string;
  reason?: string;
}

interface GLContextValue {
  accounts: GLAccount[];
  accountById: Map<string, GLAccount>;
  journalEntries: JournalEntry[];
  periodLocks: PeriodLock[];
  isPeriodClosed: (period: string) => boolean;
  /** Post a balanced journal. Throws if invariants fail or period is closed. */
  postJournal: (input: PostJournalInput) => JournalEntry;
  /** Append a reversing entry to void a posted JE. */
  reverseJournal: (id: string, reason: string, by?: string) => JournalEntry;
  /** Close a period: roll revenue+expense → Retained Earnings (3900) and freeze postings. */
  closePeriod: (period: string, by?: string, reason?: string) => PeriodLock;
  /** Reopen a closed period: reverse the RE roll and unfreeze. */
  reopenPeriod: (period: string, reason: string, by?: string) => PeriodLock;
  /** Trial balance for a single period (yyyy-mm) or all-time. */
  getTrialBalance: (period?: string) => AccountBalance[];
  /** Sorted journal lines for one account (most recent first). */
  getAccountLedger: (
    accountId: string,
    period?: string,
  ) => Array<{ entry: JournalEntry; line: JournalLine; runningBalance: number }>;
}

const GLContext = createContext<GLContextValue | null>(null);

export function GeneralLedgerProvider({ children }: { children: React.ReactNode }) {
  const [accounts] = useState<GLAccount[]>(() => SEED_CHART_OF_ACCOUNTS);
  const [journalEntries, setJournalEntries] = useState<JournalEntry[]>([]);
  const [periodLocks, setPeriodLocks] = useState<PeriodLock[]>([]);
  const counterRef = useRef(0);

  const isPeriodClosed = useCallback(
    (period: string) => periodLocks.some(p => p.period === period && p.status === "closed"),
    [periodLocks],
  );

  const accountById = useMemo(() => {
    const m = new Map<string, GLAccount>();
    accounts.forEach(a => m.set(a.id, a));
    return m;
  }, [accounts]);

  const nextJeNumber = useCallback((postingDate: string) => {
    const yr = postingDate.slice(0, 4);
    // Re-seed counter from the highest existing JE-{yr}-###### so refresh,
    // HMR, or out-of-order seeding never collide on numbers.
    const prefix = `JE-${yr}-`;
    let maxExisting = 0;
    for (const je of journalEntries) {
      if (!je.number.startsWith(prefix)) continue;
      const n = Number(je.number.slice(prefix.length));
      if (Number.isFinite(n) && n > maxExisting) maxExisting = n;
    }
    const next = Math.max(counterRef.current, maxExisting) + 1;
    counterRef.current = next;
    return `${prefix}${String(next).padStart(6, "0")}`;
  }, [journalEntries]);


  const postJournal = useCallback((input: PostJournalInput): JournalEntry => {
    // Idempotency: short-circuit if a non-void JE with this key already exists.
    const dedupeKey = deriveIdempotencyKey(input);
    if (dedupeKey) {
      const existing = journalEntries.find(
        j => j.idempotencyKey === dedupeKey && j.status !== "void",
      );
      if (existing) return existing;
    }
    const period = ymKey(input.postingDate);
    if (input.source !== "adjustment" || !input.sourceRef || input.sourceRef.kind !== "period_reopen") {
      if (periodLocks.some(p => p.period === period && p.status === "closed")) {
        throw new Error(`Period ${period} is closed. Reopen it before posting.`);
      }
    }
    if (!input.lines || input.lines.length < 2) {
      throw new Error("Journal entry requires at least two lines.");
    }
    let dr = 0;
    let cr = 0;
    const linesValidated: JournalLine[] = input.lines.map((l, i) => {
      const acc = accountById.get(l.accountId);
      if (!acc) throw new Error(`Unknown account: ${l.accountId}`);
      if (!acc.active) throw new Error(`Account ${acc.number} ${acc.name} is inactive.`);
      // COA guardrail: roll-up parents are reporting buckets only — postings
      // must target a trade-level child so trial balance + P&L stay decomposed.
      if (ROLLUP_PARENT_ACCOUNTS.has(acc.id)) {
        throw new Error(`Account ${acc.number} ${acc.name} is a rollup parent — post to a trade-level child instead.`);
      }
      if (l.amount <= 0) throw new Error(`Line ${i + 1}: amount must be positive.`);
      if (acc.requiresSubledger === "customer" && !l.customerId) {
        throw new Error(`Account ${acc.number} requires a customer reference.`);
      }
      if (acc.requiresSubledger === "vendor" && !l.vendorId) {
        throw new Error(`Account ${acc.number} requires a vendor reference.`);
      }
      if (acc.requiresSubledger === "job" && !l.jobId) {
        throw new Error(`Account ${acc.number} requires a job (WO#) reference.`);
      }
      if (l.side === "debit") dr += l.amount; else cr += l.amount;
      return {
        id: `jl_${Date.now()}_${i}_${Math.random().toString(36).slice(2, 7)}`,
        accountId: l.accountId,
        amount: l.amount,
        side: l.side,
        memo: l.memo,
        customerId: l.customerId,
        vendorId: l.vendorId,
        jobId: l.jobId,
        tradeType: l.tradeType ?? acc.tradeType,
        wipBucket: l.wipBucket,
      };
    });
    if (CENT(dr) !== CENT(cr)) {
      throw new Error(`Out of balance: DR $${dr.toFixed(2)} ≠ CR $${cr.toFixed(2)}.`);
    }
    // FX guard — stamp must be a 3-letter uppercase ISO-4217 code. JEs are
    // single-currency today; per-currency balance is trivially satisfied, but
    // validating the stamp now prevents lowercase/whitespace drift sneaking
    // into the Supabase column once multi-currency lands.
    const ccy = (input.currency ?? DEFAULT_CURRENCY).trim().toUpperCase();
    if (!/^[A-Z]{3}$/.test(ccy)) {
      throw new Error(`Invalid currency code: ${input.currency}. Expected 3-letter ISO-4217 (e.g. USD).`);
    }
    // Idempotency hygiene — every non-manual JE should carry a dedupe key.
    // Auto-derivation already covers source+sourceRef; warn on the gap so
    // it's visible during dev and shows up in the audit log later.
    if (!dedupeKey && input.source && input.source !== "manual" && input.source !== "adjustment") {
      // eslint-disable-next-line no-console
      console.warn(`[GL] ${input.source} posting has no idempotencyKey — pass sourceRef or set idempotencyKey explicitly.`);
    }
    const now = new Date().toISOString();
    const entry: JournalEntry = {
      id: `je_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
      number: nextJeNumber(input.postingDate),
      postingDate: input.postingDate,
      period: ymKey(input.postingDate),
      source: input.source ?? "manual",
      description: input.description,
      status: "posted",
      lines: linesValidated,
      currency: ccy,
      createdAt: now,
      createdBy: input.createdBy ?? "Steve",
      postedAt: now,
      postedBy: input.createdBy ?? "Steve",
      sourceRef: input.sourceRef,
      idempotencyKey: dedupeKey,
    };
    setJournalEntries(prev => [entry, ...prev]);
    return entry;
  }, [accountById, nextJeNumber, periodLocks, journalEntries]);

  const reverseJournal = useCallback((id: string, reason: string, by = "Steve") => {
    const orig = journalEntries.find(j => j.id === id);
    if (!orig) throw new Error("Journal entry not found.");
    if (orig.status !== "posted") throw new Error("Only posted entries can be reversed.");
    // Block reversing a reversal — that would create an oscillating chain
    // that's nearly impossible to audit. To "undo" a reversal, reopen the
    // period or post a fresh corrective JE.
    if (orig.sourceRef?.kind === "reversal_of" || orig.sourceRef?.kind === "period_reopen") {
      throw new Error("Cannot reverse a reversal entry. Post a corrective JE instead.");
    }
    // Reversal date policy:
    //   • If the original's period is OPEN, post on today (normal correction).
    //   • If the original's period is CLOSED, walk forward to the first open
    //     period and post on its first day. This keeps audited periods sealed
    //     and lands the offset cleanly in the next-available accounting month
    //     rather than smearing it onto an unrelated current day.
    const todayIso = new Date().toISOString().slice(0, 10);
    let postingDate = todayIso;
    if (isPeriodClosed(orig.period)) {
      let candidate = nextPeriod(orig.period);
      // Cap the walk at 240 months (20 yrs) to guarantee termination.
      for (let i = 0; i < 240 && isPeriodClosed(candidate); i += 1) {
        candidate = nextPeriod(candidate);
      }
      postingDate = firstDayOfPeriod(candidate);
      // If today is already past that first-of-period date AND still open,
      // prefer today so the audit trail timestamp matches the user's action.
      if (todayIso > postingDate && !isPeriodClosed(todayIso.slice(0, 7))) {
        postingDate = todayIso;
      }
    }
    const reversal = postJournal({
      postingDate,
      description: `Reversal of ${orig.number} — ${reason}`,
      source: "adjustment",
      sourceRef: { kind: "reversal_of", id: orig.id },
      createdBy: by,
      lines: orig.lines.map(l => ({
        accountId: l.accountId,
        amount: l.amount,
        side: l.side === "debit" ? "credit" : "debit",
        memo: l.memo,
        customerId: l.customerId,
        vendorId: l.vendorId,
        jobId: l.jobId,
        tradeType: l.tradeType,
      })),
    });
    setJournalEntries(prev => prev.map(j =>
      j.id === id
        ? { ...j, status: "void", voidedAt: reversal.postedAt, voidedBy: by, voidReason: reason }
        : j,
    ));
    return reversal;
  }, [journalEntries, postJournal, isPeriodClosed]);

  const getTrialBalance = useCallback((period?: string): AccountBalance[] => {
    const totals = new Map<string, { debit: number; credit: number }>();
    accounts.forEach(a => totals.set(a.id, { debit: 0, credit: 0 }));
    for (const je of journalEntries) {
      if (je.status !== "posted") continue;
      if (period && je.period !== period) continue;
      for (const l of je.lines) {
        const t = totals.get(l.accountId);
        if (!t) continue;
        if (l.side === "debit") t.debit += l.amount;
        else t.credit += l.amount;
      }
    }
    return accounts.map(a => {
      const t = totals.get(a.id)!;
      const net = a.normalBalance === "debit" ? t.debit - t.credit : t.credit - t.debit;
      return { accountId: a.id, debit: t.debit, credit: t.credit, net };
    });
  }, [accounts, journalEntries]);

  const getAccountLedger = useCallback((accountId: string, period?: string) => {
    const acc = accountById.get(accountId);
    if (!acc) return [];
    const rows: Array<{ entry: JournalEntry; line: JournalLine }> = [];
    for (const je of journalEntries) {
      if (je.status !== "posted") continue;
      if (period && je.period !== period) continue;
      for (const l of je.lines) {
        if (l.accountId === accountId) rows.push({ entry: je, line: l });
      }
    }
    rows.sort((a, b) => a.entry.postingDate.localeCompare(b.entry.postingDate)
      || a.entry.number.localeCompare(b.entry.number));
    let running = 0;
    const withRunning = rows.map(({ entry, line }) => {
      const delta = acc.normalBalance === "debit"
        ? (line.side === "debit" ? line.amount : -line.amount)
        : (line.side === "credit" ? line.amount : -line.amount);
      running += delta;
      return { entry, line, runningBalance: running };
    });
    return withRunning.reverse();
  }, [accountById, journalEntries]);


  const closePeriod = useCallback((period: string, by = "Steve", reason?: string): PeriodLock => {
    if (periodLocks.some(p => p.period === period && p.status === "closed")) {
      throw new Error(`Period ${period} is already closed.`);
    }
    // Refuse to close a future period — accountants close historical months,
    // never the one we haven't lived yet.
    const currentYm = new Date().toISOString().slice(0, 7);
    if (period > currentYm) {
      throw new Error(`Cannot close future period ${period}. Current period is ${currentYm}.`);
    }
    // Build retained-earnings roll: zero out revenue + expense → 3900.
    const totals = new Map<string, { debit: number; credit: number }>();
    for (const je of journalEntries) {
      if (je.status !== "posted") continue;
      if (je.period !== period) continue;
      for (const l of je.lines) {
        const acc = accountById.get(l.accountId);
        if (!acc) continue;
        if (acc.type !== "revenue" && acc.type !== "expense") continue;
        const t = totals.get(l.accountId) ?? { debit: 0, credit: 0 };
        if (l.side === "debit") t.debit += l.amount; else t.credit += l.amount;
        totals.set(l.accountId, t);
      }
    }
    const rollLines: NewLineInput[] = [];
    let netCreditToRE = 0;
    for (const [accountId, t] of totals) {
      const acc = accountById.get(accountId)!;
      const net = acc.normalBalance === "debit" ? t.debit - t.credit : t.credit - t.debit;
      if (Math.round(net * 100) === 0) continue;
      if (acc.type === "revenue") {
        // Revenue has credit balance — debit to zero.
        rollLines.push({ accountId, amount: Math.abs(net), side: net >= 0 ? "debit" : "credit" });
        netCreditToRE += net;
      } else {
        // Expense has debit balance — credit to zero.
        rollLines.push({ accountId, amount: Math.abs(net), side: net >= 0 ? "credit" : "debit" });
        netCreditToRE -= net;
      }
    }
    let rollEntryId: string | undefined;
    if (rollLines.length > 0 && Math.round(netCreditToRE * 100) !== 0) {
      rollLines.push({
        accountId: "3900",
        amount: Math.abs(netCreditToRE),
        side: netCreditToRE >= 0 ? "credit" : "debit",
        memo: `Net income roll for ${period}`,
      });
      // Use last day of period as posting date (UTC-safe).
      const lastDay = lastDayOfPeriod(period);
      const je = postJournal({
        postingDate: lastDay,
        description: `Period close — retained earnings roll for ${period}`,
        source: "adjustment",
        sourceRef: { kind: "period_close", id: period },
        createdBy: by,
        lines: rollLines,
      });
      rollEntryId = je.id;
    }
    const lock: PeriodLock = {
      period, status: "closed", closedAt: new Date().toISOString(), closedBy: by, rollEntryId, reason,
    };
    setPeriodLocks(prev => [...prev.filter(p => p.period !== period), lock]);
    return lock;
  }, [accountById, journalEntries, periodLocks, postJournal]);

  const reopenPeriod = useCallback((period: string, reason: string, by = "Steve"): PeriodLock => {
    const lock = periodLocks.find(p => p.period === period && p.status === "closed");
    if (!lock) throw new Error(`Period ${period} is not closed.`);
    if (lock.rollEntryId) {
      const orig = journalEntries.find(j => j.id === lock.rollEntryId);
      if (orig && orig.status === "posted") {
        // Post the reversal on the last day of the reopened period (not today)
        // so net income is restored to the correct period instead of distorting
        // current-period P&L. Bypass closed-period guard via period_reopen tag.
        const lastDay = lastDayOfPeriod(period);
        const reversal = postJournal({
          postingDate: lastDay,
          description: `Period reopen — reverse RE roll for ${period}`,
          source: "adjustment",
          sourceRef: { kind: "period_reopen", id: period },
          createdBy: by,
          lines: orig.lines.map(l => ({
            accountId: l.accountId,
            amount: l.amount,
            side: l.side === "debit" ? "credit" : "debit",
            memo: l.memo,
          })),
        });
        setJournalEntries(prev => prev.map(j =>
          j.id === orig.id
            ? { ...j, status: "void", voidedAt: reversal.postedAt, voidedBy: by, voidReason: `Period ${period} reopened: ${reason}` }
            : j,
        ));
      }
    }
    const next: PeriodLock = { period, status: "open", reason };
    setPeriodLocks(prev => prev.map(p => (p.period === period ? next : p)));
    return next;
  }, [journalEntries, periodLocks, postJournal]);

  const value = useMemo<GLContextValue>(() => ({
    accounts,
    accountById,
    journalEntries,
    periodLocks,
    isPeriodClosed,
    postJournal,
    reverseJournal,
    closePeriod,
    reopenPeriod,
    getTrialBalance,
    getAccountLedger,
  }), [accounts, accountById, journalEntries, periodLocks, isPeriodClosed, postJournal, reverseJournal, closePeriod, reopenPeriod, getTrialBalance, getAccountLedger]);

  return <GLContext.Provider value={value}>{children}</GLContext.Provider>;
}

export function useGeneralLedger() {
  const ctx = useContext(GLContext);
  if (!ctx) throw new Error("useGeneralLedger must be used inside <GeneralLedgerProvider>");
  return ctx;
}
